import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type DefaultBackground = 'Esri (licht)' | 'Luchtfoto' | 'OpenStreetMap'
export type DetectTheme = 'blue' | 'forest' | 'earth' | 'purple'

export interface SettingsState {
  // Kaart
  defaultBackground: DefaultBackground
  showScaleBar: boolean

  // Veld
  fieldModeEnabled: boolean
  fieldModeOfflineLabels: boolean

  // GPS
  gpsAutoStart: boolean
  showAccuracyCircle: boolean

  // Feedback
  hapticFeedback: boolean

  // Vondsten
  vondstenLocalOnly: boolean  // true = localStorage, false = Firebase
  showVondstButton: boolean   // Show/hide the add vondst button

  // Route opnemen
  showRouteRecordButton: boolean  // Show/hide the route record button

  // Zichtbaarheid op kaart
  showLocalVondsten: boolean  // Show/hide local vondsten markers on map
  showCustomPointLayers: boolean  // Show/hide custom point layers on map

  // Weergave
  fontScale: number  // 80-150, percentage scale for app text
  uiTheme: DetectTheme  // Gedeeld kleurenschema voor alle vensters
  layerPanelFontScale: number  // 80-150, for Kaartlagen panel
  presetPanelFontScale: number  // 80-150, for Presets panel
  menuFontScale: number  // 80-130, for Hamburger menu
  weatherFontScale: number  // 80-150, for Weather widget

  // Voice feedback
  voiceFeedbackEnabled: boolean  // Speak aloud when adding vondsten

  // Stappenteller
  showStepCounter: boolean  // Show/hide the step counter

  // Weergave opties
  showFontSliders: boolean  // Show/hide font size sliders (boomer mode)

  // Weer
  showWeatherButton: boolean  // Show/hide the weather button

  // Welkom modal
  hideWelcomeModal: boolean  // Don't show welcome modal on startup

  // Gereedschappen (linkerkant)
  showMeasureTool: boolean  // Show/hide measure tool button
  showDrawTool: boolean     // Show/hide draw tool button
  showPrintTool: boolean    // Show/hide print tool button

  // Actions
  setDefaultBackground: (bg: DefaultBackground) => void
  setShowScaleBar: (value: boolean) => void
  setFieldModeEnabled: (value: boolean) => void
  setFieldModeOfflineLabels: (value: boolean) => void
  setGpsAutoStart: (value: boolean) => void
  setShowAccuracyCircle: (value: boolean) => void
  setHapticFeedback: (value: boolean) => void
  setVondstenLocalOnly: (value: boolean) => void
  setShowVondstButton: (value: boolean) => void
  setShowRouteRecordButton: (value: boolean) => void
  setShowLocalVondsten: (value: boolean) => void
  setShowCustomPointLayers: (value: boolean) => void
  setFontScale: (value: number) => void
  setUiTheme: (value: DetectTheme) => void
  setLayerPanelFontScale: (value: number) => void
  setPresetPanelFontScale: (value: number) => void
  setMenuFontScale: (value: number) => void
  setWeatherFontScale: (value: number) => void
  setVoiceFeedbackEnabled: (value: boolean) => void
  setShowStepCounter: (value: boolean) => void
  setShowFontSliders: (value: boolean) => void
  setShowWeatherButton: (value: boolean) => void
  setHideWelcomeModal: (value: boolean) => void
  setShowMeasureTool: (value: boolean) => void
  setShowDrawTool: (value: boolean) => void
  setShowPrintTool: (value: boolean) => void
}

export const CLOUD_SETTINGS_KEYS = [
  'defaultBackground',
  'showScaleBar',
  'fieldModeEnabled',
  'fieldModeOfflineLabels',
  'gpsAutoStart',
  'showAccuracyCircle',
  'hapticFeedback',
  'vondstenLocalOnly',
  'showVondstButton',
  'showRouteRecordButton',
  'showLocalVondsten',
  'showCustomPointLayers',
  'fontScale',
  'uiTheme',
  'layerPanelFontScale',
  'presetPanelFontScale',
  'menuFontScale',
  'weatherFontScale',
  'voiceFeedbackEnabled',
  'showStepCounter',
  'showFontSliders',
  'showWeatherButton',
  'hideWelcomeModal',
  'showMeasureTool',
  'showDrawTool',
  'showPrintTool'
] as const

export type CloudSettings = Pick<SettingsState, typeof CLOUD_SETTINGS_KEYS[number]>

export function getCloudSettings(state: SettingsState = useSettingsStore.getState()): CloudSettings {
  return Object.fromEntries(
    CLOUD_SETTINGS_KEYS.map((key) => [key, state[key]])
  ) as CloudSettings
}

export function applyCloudSettings(settings: Partial<CloudSettings>): void {
  const allowedValues = Object.fromEntries(
    CLOUD_SETTINGS_KEYS
      .filter((key) => Object.prototype.hasOwnProperty.call(settings, key))
      .map((key) => [key, settings[key]])
  ) as Partial<CloudSettings>

  useSettingsStore.setState(allowedValues)
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      // Defaults
      defaultBackground: 'Esri (licht)',
      showScaleBar: true,
      fieldModeEnabled: false,
      fieldModeOfflineLabels: true,
      gpsAutoStart: false,
      showAccuracyCircle: true,
      hapticFeedback: true,
      vondstenLocalOnly: true,  // Default to local storage (no login needed)
      showVondstButton: false,  // Hidden by default, can enable in settings
      showRouteRecordButton: false,  // Hidden by default, can enable in settings
      showLocalVondsten: true,  // Show vondsten markers by default
      showCustomPointLayers: true,  // Show custom point layers by default
      fontScale: 100,           // Default 100% = 14px base
      uiTheme: 'blue',          // Rustige Detect-huisstijl
      layerPanelFontScale: 100, // Default 100%
      presetPanelFontScale: 100, // Default 100%
      menuFontScale: 100,       // Default 100%
      weatherFontScale: 100,    // Default 100%
      voiceFeedbackEnabled: false, // Off by default
      showStepCounter: false, // Hidden by default, can enable in settings
      showFontSliders: false, // Hidden by default (boomer mode off)
      showWeatherButton: false, // Hidden by default, can enable in settings
      hideWelcomeModal: false, // Show welcome modal by default
      showMeasureTool: true,   // Visible by default
      showDrawTool: true,      // Visible by default
      showPrintTool: true,     // Visible by default

      // Actions
      setDefaultBackground: (defaultBackground) => set({ defaultBackground }),
      setShowScaleBar: (showScaleBar) => set({ showScaleBar }),
      setFieldModeEnabled: (fieldModeEnabled) => set({ fieldModeEnabled }),
      setFieldModeOfflineLabels: (fieldModeOfflineLabels) => set({ fieldModeOfflineLabels }),
      setGpsAutoStart: (gpsAutoStart) => set({ gpsAutoStart }),
      setShowAccuracyCircle: (showAccuracyCircle) => set({ showAccuracyCircle }),
      setHapticFeedback: (hapticFeedback) => set({ hapticFeedback }),
      setVondstenLocalOnly: (vondstenLocalOnly) => set({ vondstenLocalOnly }),
      setShowVondstButton: (showVondstButton) => set({ showVondstButton }),
      setShowRouteRecordButton: (showRouteRecordButton) => set({ showRouteRecordButton }),
      setShowLocalVondsten: (showLocalVondsten) => set({ showLocalVondsten }),
      setShowCustomPointLayers: (showCustomPointLayers) => set({ showCustomPointLayers }),
      setFontScale: (fontScale) => set({ fontScale }),
      setUiTheme: (uiTheme) => set({ uiTheme }),
      setLayerPanelFontScale: (layerPanelFontScale) => set({ layerPanelFontScale }),
      setPresetPanelFontScale: (presetPanelFontScale) => set({ presetPanelFontScale }),
      setMenuFontScale: (menuFontScale) => set({ menuFontScale }),
      setWeatherFontScale: (weatherFontScale) => set({ weatherFontScale }),
      setVoiceFeedbackEnabled: (voiceFeedbackEnabled) => set({ voiceFeedbackEnabled }),
      setShowStepCounter: (showStepCounter) => set({ showStepCounter }),
      setShowFontSliders: (showFontSliders) => set({ showFontSliders }),
      setShowWeatherButton: (showWeatherButton) => set({ showWeatherButton }),
      setHideWelcomeModal: (hideWelcomeModal) => set({ hideWelcomeModal }),
      setShowMeasureTool: (showMeasureTool) => set({ showMeasureTool }),
      setShowDrawTool: (showDrawTool) => set({ showDrawTool }),
      setShowPrintTool: (showPrintTool) => set({ showPrintTool })
    }),
    {
      name: 'detectorapp-settings',
      version: 5,
      migrate: (persistedState: any, version: number) => {
        const migratedState = { ...persistedState }

        // Migrate from older versions without showScaleBar
        if (version < 2) {
          migratedState.showScaleBar = true
          migratedState.fieldModeEnabled = false
          migratedState.fieldModeOfflineLabels = true
        }

        if (version < 3) {
          migratedState.fieldModeEnabled = false
          migratedState.fieldModeOfflineLabels = true
        }

        // v4: CARTO stopped serving the keyless light basemap.
        if (version < 4 && migratedState.defaultBackground === 'CartoDB (licht)') {
          migratedState.defaultBackground = 'Esri (licht)'
        }

        // v5: één vensterhuisstijl met een gedeeld kleurenschema.
        if (version < 5) {
          migratedState.uiTheme = 'blue'
        }

        return migratedState
      }
    }
  )
)
